import { createContext, useContext } from 'react'
import type {
  DraftRecord,
  ManageItemKind,
  ManageItemState,
} from '../types/manageDraftState'

export type SaveAllHandler = () => Promise<void> | void
export type DiscardAllHandler = () => Promise<void> | void

/** A view outside BoardExplorer (e.g. the Review DetailedViewScreen) that
 * carries its own unsaved-state and wants the Manage leave-guard dialog to
 * cover it too. Registered via `registerExternalPendingSource`. Callers
 * should re-register (same key) whenever `hasUnsaved` flips so the provider
 * re-runs beforeunload / guardLeave logic. */
export type ExternalPendingSource = {
  hasUnsaved: boolean
  /** Persist everything this view has pending. */
  saveAll: () => Promise<void> | void
  /** Drop everything this view has pending, reverting to baseline. */
  discardAll: () => Promise<void> | void
}

export type LeaveDialogRequest = {
  resolve: (proceed: boolean) => void
}

export interface ManageDraftContextValue {
  records: ReadonlyMap<string, DraftRecord>
  hasUnsavedDrafts: boolean
  draftCount: number
  modifiedCount: number

  getState(id: string): ManageItemState
  getRecord(id: string): DraftRecord | undefined

  /** Track a brand-new item that was just added to Manage. */
  markDraft(record: Omit<DraftRecord, 'state'>): void
  /** Track an edit to an already-saved item; pass a snapshot for revert. */
  markModified(
    id: string,
    kind: ManageItemKind,
    parentId: string | null,
    originalSnapshot: unknown,
  ): void
  /** Seed one or more AI-proposed edits atomically. Each record lands as
   * 'modified' with both originalSnapshot (for revert) and proposedFields
   * (for BoardExplorer to hydrate its per-entry pendingChanges on select). */
  seedProposedEdits(
    edits: ReadonlyArray<{
      id: string
      kind: ManageItemKind
      parentId: string | null
      originalSnapshot: unknown
      proposedFields: Record<string, unknown>
    }>,
  ): void
  /** A pending item just successfully persisted — drop it from the registry. */
  markSaved(id: string): void
  markSavedMany(ids: ReadonlyArray<string>): void
  /** Forget about an item entirely (used by discard + cascade cleanup). */
  clearRecord(id: string): void
  clearMany(ids: ReadonlyArray<string>): void
  clearAll(): void

  /** Step-2 wiring: the Manage screen registers handlers it can run when the
   * leave dialog's Save-all / Discard-all is chosen. */
  setHandlers(handlers: {
    saveAll?: SaveAllHandler
    discardAll?: DiscardAllHandler
  }): void

  /** Register a secondary Manage view (e.g. Review) that carries its own
   * unsaved state. hasUnsavedDrafts and guardLeave union across every
   * registered source; leave-dialog Save/Discard fans out to each source's
   * saveAll/discardAll after the primary Board handler runs. Returns an
   * unregister function — call it from the source's cleanup. */
  registerExternalPendingSource(key: string, source: ExternalPendingSource): () => void

  /** Returns true if it's safe to leave (no drafts anywhere, or user confirmed). */
  guardLeave(): Promise<boolean>
}

export const ManageDraftContext = createContext<ManageDraftContextValue | null>(null)

export const useManageDraft = (): ManageDraftContextValue => {
  const ctx = useContext(ManageDraftContext)
  if (!ctx) throw new Error('useManageDraft must be used inside ManageDraftProvider')
  return ctx
}

/** Lenient hook for places that may render outside the provider (rare). */
export const useOptionalManageDraft = (): ManageDraftContextValue | null =>
  useContext(ManageDraftContext)
